import { z } from "zod";

/**
 * Zod is the single source of truth for the domain. Every model is a schema;
 * the TypeScript types are *inferred* from the schemas (via `z.infer`), so the
 * types and the runtime validation can never drift apart. Data crossing a
 * boundary — the Yahoo API, a stored snapshot, the browser fetch — is parsed
 * through these before the rest of the code touches it.
 */

// A roster slot (lineup position) maps to the set of base positions that may
// fill it. Flex slots accept several; dedicated slots accept one.
export const SlotEligibility = z.record(z.string(), z.array(z.string()));
export type SlotEligibility = z.infer<typeof SlotEligibility>;

export const DEFAULT_SLOT_ELIGIBILITY: SlotEligibility = {
  QB: ["QB"],
  RB: ["RB"],
  WR: ["WR"],
  TE: ["TE"],
  K: ["K"],
  DEF: ["DEF"],
  "W/R": ["WR", "RB"],
  "W/T": ["WR", "TE"],
  "W/R/T": ["WR", "RB", "TE"],
  "Q/W/R/T": ["QB", "WR", "RB", "TE"], // superflex
};

// Positions that never count toward the starting lineup.
export const BENCH_POSITIONS = new Set(["BN", "IR", "NA"]);

export const Player = z.object({
  playerId: z.string(),
  name: z.string(),
  /** positions this player is eligible for, e.g. ["RB"] or ["RB","WR"] */
  positions: z.array(z.string()).min(1),
  /** fantasy points scored that week (already calculated by Yahoo) */
  points: z.number(),
  /** where the manager actually played them: "QB", "W/R/T", "BN", ... */
  selectedPosition: z.string(),
  nflTeam: z.string().default(""),
});
export type Player = z.infer<typeof Player>;

export const Team = z.object({
  teamId: z.string(),
  name: z.string(),
  manager: z.string().default(""),
});
export type Team = z.infer<typeof Team>;

export const TeamWeek = z.object({
  teamId: z.string(),
  week: z.number().int().positive(),
  players: z.array(Player),
  /**
   * Points the manager actually scored. When present it's authoritative
   * (straight from Yahoo); when null it's derived from started players.
   */
  actualPoints: z.number().nullable().default(null),
});
export type TeamWeek = z.infer<typeof TeamWeek>;

export const Matchup = z.object({
  status: z.enum(["scheduled", "in_progress", "final"]).optional(),
  week: z.number().int().positive(),
  teamA: z.string(),
  teamB: z.string(),
});
export type Matchup = z.infer<typeof Matchup>;

export const RosterSlots = z.record(z.string(), z.number().int().nonnegative());
export type RosterSlots = z.infer<typeof RosterSlots>;

/** Everything a provider returns and a snapshot stores. */
export const LeagueData = z.object({
  leagueName: z.string(),
  season: z.number().int(),
  rosterSlots: RosterSlots,
  slotEligibility: SlotEligibility.default(DEFAULT_SLOT_ELIGIBILITY),
  teams: z.array(Team),
  matchups: z.array(Matchup),
  teamWeeks: z.array(TeamWeek),
});
export type LeagueData = z.infer<typeof LeagueData>;

// --- computed outputs --------------------------------------------------------

export const OptimalSlot = z.object({
  slot: z.string(),
  player: Player.nullable(),
});
export type OptimalSlot = z.infer<typeof OptimalSlot>;

export const StandingRow = z.object({
  team: Team,
  wins: z.number().int().nonnegative(),
  losses: z.number().int().nonnegative(),
  ties: z.number().int().nonnegative(),
  pointsFor: z.number(),
  pointsAgainst: z.number(),
});
export type StandingRow = z.infer<typeof StandingRow>;

// --- the persisted snapshot --------------------------------------------------

export const SNAPSHOT_SCHEMA_VERSION = 1 as const;

export const Snapshot = z.object({
  schemaVersion: z.literal(SNAPSHOT_SCHEMA_VERSION),
  generatedAt: z.string(), // ISO timestamp
  league: LeagueData,
  polling: z.object({
    week: z.number().int().positive(),
    pendingFinal: z.boolean(),
    checkedAt: z.string(),
  }).optional(),
});
export type Snapshot = z.infer<typeof Snapshot>;
